import type { FC } from 'react';
import { motion } from 'framer-motion';

import { heroReveal } from '#/lib/hero-reveal';
import type { SanityHeroHome } from '#/lib/sanity/queries/heroHome';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { SectionMascot } from '#/components/BlobMascot';
import { Button } from '#/components/Button';
import { FormCustom } from '#/components/Form/Custom';
import { HomepageEmailForm } from '#/components/Form/HomepageEmail';
import { formatHeadline } from '#/components/Headline';
import {
  HeroBackgroundGraphic,
  HOME_HERO_BACKGROUND,
} from '#/components/HeroBackgroundGraphic';

/**
 * Home page hero ("Website 2026"). Always sits on the dark home surface —
 * there is no theme selector, and the background graphic is always present
 * (it is not CMS-selectable).
 */
export const HeroHome: FC<SanityHeroHome & PageLayoutSharedProps> = (props) => {
  const isFirst = props.sectionIndex === 0;

  return (
    <div
      className={cn(
        'p-main bg-charcoal text-cloud relative isolate overflow-hidden',
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-60 pt-[calc(var(--header-height)+8rem)] m:pb-100'
          : 'py-60 m:py-100',
      )}
    >
      {/* Home hero anchors left on mobile so the quieter side of the graphic
          sits behind the stacked content; desktop keeps the right anchor. */}
      <HeroBackgroundGraphic
        background={HOME_HERO_BACKGROUND}
        className="object-left-bottom m:object-right-bottom"
      />
      <SectionMascot preset={props.mascot} />
      <div className="relative z-[1] mx-auto flex max-w-1280 flex-col">
        {/* pre-line renders editor line breaks from the multiline title field */}
        <motion.h1
          {...heroReveal(0)}
          className="type-heading3 m:type-heading2 l:type-heading1 xl:text-82 max-w-[1100px] whitespace-pre-line text-balance"
          data-sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'title',
          )}
        >
          {formatHeadline(props.title, 'text-electric')}
        </motion.h1>

        {props.subtitle ? (
          <motion.p
            {...heroReveal(1)}
            className="type-heading5 m:type-heading4 mt-20 max-w-[640px] text-pretty m:mt-32"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'subtitle',
            )}
          >
            {formatHeadline(props.subtitle, 'text-electric')}
          </motion.p>
        ) : null}

        <motion.div
          {...heroReveal(2)}
          className="mt-40 flex flex-col gap-16 m:mt-60 m:flex-row m:items-start m:gap-32"
        >
          {props.formFunnel === 'rails' ||
          props.form?.formType === 'contact' ? (
            <HomepageEmailForm
              className="w-full max-w-[500px]"
              form={props.form?.formType === 'contact' ? props.form : null}
              formFunnel={props.formFunnel}
              primaryButtonLabel={props.primaryButtonLabel}
              theme="dark"
            />
          ) : props.form ? (
            <FormCustom
              className="w-full max-w-[500px]"
              form={props.form}
              confirmation={props.formConfirmation ?? ''}
              theme="dark"
            />
          ) : null}

          {/* Contact funnel: optional secondary CMS link beside the email capture. */}
          {props.formFunnel !== 'rails' && props.secondaryButton?.href ? (
            <Button
              variant="secondary"
              theme="dark"
              href={props.secondaryButton.href}
              target={props.secondaryButton.target}
              // h-50 + flex centering matches the enriched form's input row
              // height so the two sit flush in the row. Desktop only.
              className="hidden h-50 items-center justify-center self-start text-center m:flex"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'secondaryButton',
              )}
            >
              {props.secondaryButton.title}
            </Button>
          ) : null}
        </motion.div>

        {props.subtextLink?.href ? (
          <motion.div
            {...heroReveal(3)}
            className="mt-24 m:mt-48"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'subtextLink',
            )}
          >
            <Button
              variant="link"
              theme="dark"
              href={props.subtextLink.href}
              target={props.subtextLink.target}
            >
              {props.subtextLink.title}
            </Button>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};
